/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Message, { IMessage } from './../../message';
import { IDescriptorBody } from '../../descriptor';
import OperationMetadataHelpers from './../helpers/operationmetadatahelpers';
import OperationMetadataDescriptor, { IOperationMetadata, IOperationMetadataJSON } from '../descriptors/operationmetadatadescriptor';

export interface ICollaborativeEditingResponse extends IMessage {
	version: number;
	buffers: Buffer[];
	types: number[];
	metadata: IOperationMetadataJSON[];
	lastOperationId: string;
	lastOperationAcceptedAt: string | null;
}

interface ICollaborativeEditingResponseParams {
	version: number;
	buffers?: Buffer[];
	types?: number[];
	metadata?: IOperationMetadata[];
	lastOperationId?: string;
	lastOperationAcceptedAt?: string | null;
}

interface ICollaborativeEditingResponseData {
	buffers: Buffer[];
	types: number[];
	baseVersion: number;
	lastOperationId: string;
	lastOperationAcceptedAt: Date | null;
}

export default class CollaborativeEditingResponse extends Message {
	public static readonly DESCRIPTOR_NAME: string = 'CollaborativeEditingResponse';

	public static readonly DESCRIPTOR: IDescriptorBody = {

		fields: {
			version: {
				type: 'uint32',
				id: 1
			},
			buffers: {
				rule: 'repeated',
				type: 'bytes',
				id: 2
			},
			types: {
				rule: 'repeated',
				type: 'uint32',
				id: 3
			},
			metadata: {
				rule: 'repeated',
				type: 'OperationMetadataDescriptor',
				id: 5
			},
			lastOperationId: {
				type: 'string',
				id: 6
			},
			lastOperationAcceptedAt: {
				type: 'string',
				id: 7
			}
		}
	};

	/**
	 * @deprecated - Use CollaborativeEditingResponse.create factory method instead.
	 * @param version - Version of the document.
	 * @param buffers - List of operations compressed to the binary format.
	 * @param types - List of compressor identifiers.
	 * @param metadata - Metadata of changes.
	 */
	public constructor(
		public readonly version: number,
		public readonly buffers: Buffer[] = [],
		public readonly types: number[] = [],
		public readonly metadata: IOperationMetadata[] = [],
		public readonly lastOperationId: string = '',
		public readonly lastOperationAcceptedAt: Date | null = null
	) {
		super();
	}

	public get data(): ICollaborativeEditingResponseData {
		return {
			buffers: this.buffers,
			types: this.types,
			baseVersion: this.version - this.types.length,
			lastOperationId: this.lastOperationId,
			lastOperationAcceptedAt: this.lastOperationAcceptedAt
		};
	}

	public get wereChangesApplied(): boolean {
		return !( this.types?.length );
	}

	public toJSON(): ICollaborativeEditingResponse {
		return {
			version: this.version,
			buffers: this.buffers,
			types: this.types,
			metadata: OperationMetadataHelpers
				.removeUnnecessaryMetadata( this.types, this.metadata )
				.map( OperationMetadataDescriptor.toJSON ),
			lastOperationId: this.lastOperationId,
			// instanceof operator can be safely used on node constructors e.g Date
			// eslint-disable-next-line no-restricted-syntax
			lastOperationAcceptedAt: this.lastOperationAcceptedAt instanceof Date ?
				this.lastOperationAcceptedAt.toISOString() :
				this.lastOperationAcceptedAt
		};
	}

	public static fromJSON( data: ICollaborativeEditingResponse ): CollaborativeEditingResponse {
		return new CollaborativeEditingResponse(
			data.version,
			data.buffers,
			data.types,
			OperationMetadataHelpers
				.prepareMetadataForOperations( data.types, data.metadata )
				.map( OperationMetadataDescriptor.fromJSON ),
			data.lastOperationId,
			data.lastOperationAcceptedAt ? new Date( data.lastOperationAcceptedAt ) : null
		);
	}

	public static create( data: ICollaborativeEditingResponseParams ): CollaborativeEditingResponse {
		return new CollaborativeEditingResponse(
			data.version,
			data.buffers,
			data.types,
			data.metadata,
			data.lastOperationId,
			data.lastOperationAcceptedAt ? new Date( data.lastOperationAcceptedAt ) : null
		);
	}
}
